/**
 * okrTrackerSyncService.ts
 *
 * Reads OKRs assigned to a person directly from the OKR Tracker
 * (`okr.rascalrepublic.com`). Both apps share `okr-tracker-staging`
 * Firestore — no HTTP API needed, no auth changes; the /users and
 * /keyResults collections already allow read-if-signedIn.
 *
 * MVP scope (this iteration):
 *   - Lookup user by email → resolves to Firebase auth UID
 *   - Fetch keyResults where ownerId === resolved UID
 *   - Resolve each KR's parent objective to filter by cycle year
 *   - Quarter-overlap filter based on EPR cycle period
 *   - Returns SyncedKr[] ready to be mapped to OkrSeed[]
 *
 * Out of scope for MVP (queued as follow-up tasks):
 *   - Dept Lead bucket (leadDivisionId == employee.unitId)
 *   - Support bucket (unitIds[] contains employee.unitId)
 *   - Per-quarter notification banner
 *   - Diff between launches (added / removed / changed)
 *   - Future: cascade KR assignment via magic link using email as key
 *
 * Source of truth: OKR Tracker schema reference at
 * rev/UX-OKR-Tracker/src/types/types.ts (FirestoreUser, FirestoreKeyResult,
 * FirestoreObjective).
 */

import {
  collection, query, where, getDocs, doc, getDoc, limit,
} from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { db, auth } from '../config/firebase';

/**
 * The OKR Tracker collections (/users, /keyResults, /objectives,
 * /cascadedTasks, /units) allow read-if-signedIn. Magic-link visitors are
 * not signed in, so the form signs in anonymously before syncing — the
 * same pattern the 360 magic-link flow already uses
 * (review360Service.submitResponse). The magic-link token stays the real
 * gate; the anonymous session only satisfies isSignedIn() for reads.
 */
export async function ensureAnonymousAuth(): Promise<void> {
  if (!auth.currentUser) {
    await signInAnonymously(auth);
  }
}

// ─── Shapes from OKR Tracker ────────────────────────────────────────

/**
 * Minimal projection of OKR Tracker's FirestoreUser. Keyed by Firebase
 * auth UID; email is optional in source but the Microsoft SSO path
 * always populates it. We rely on email as the cross-app join key.
 */
interface OkrTrackerUser {
  uid?: string;       // mirrors doc id; sometimes absent in source
  email?: string;
  displayName?: string;
}

/**
 * Minimal projection of OKR Tracker's FirestoreKeyResult — the fields
 * we need to surface in the EPR form. Source has more, but we ignore
 * what we don't render to keep the dependency surface small.
 */
interface OkrTrackerKeyResult {
  objectiveId: string;
  krNumber?: string;
  title: string;
  ownerId: string;
  unitId?: string;
  unitIds?: string[];
  leadDivisionId?: string;
  quarters?: string[];          // 'Q1' | 'Q2' | 'Q3' | 'Q4'
  status?: string;
  sourceOfVerification?: string;
}

/** Minimal projection of OKR Tracker's FirestoreObjective. */
interface OkrTrackerObjective {
  cycle?: string;               // year as string e.g. "2026"
  title?: string;
  archived?: boolean;
}

// ─── Outward shape (what EPR consumes) ──────────────────────────────

/**
 * Why this KR ended up in the picker:
 *   owner       → keyResults.ownerId == employee UID
 *   assigned    → cascadedTasks.{assigneeUid|assigneeEmail} match — the
 *                 employee was cascaded this KR via okr.rascalrepublic.com
 *   dept_lead   → keyResults.leadDivisionId == employee.unitId (future)
 *   support     → keyResults.unitIds[] contains employee.unitId (future)
 */
export type SyncedKrBucket = 'owner' | 'assigned' | 'dept_lead' | 'support';

export interface SyncedKr {
  /** OKR Tracker keyResult doc id — preserves linkage for Phase 2/3. */
  krId: string;
  objectiveId: string;
  objectiveTitle: string;
  krNumber: string;
  title: string;
  /** Best-effort target string (KRs often state target in title). */
  target?: string;
  quarters: Array<'Q1' | 'Q2' | 'Q3' | 'Q4'>;
  bucket: SyncedKrBucket;
}

// ─── Period → quarter map ───────────────────────────────────────────

export type EprPeriod = 'H1' | 'H2' | 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'annual';

const PERIOD_TO_QUARTERS: Record<EprPeriod, Array<'Q1' | 'Q2' | 'Q3' | 'Q4'>> = {
  H1:     ['Q1', 'Q2'],
  H2:     ['Q3', 'Q4'],
  Q1:     ['Q1'],
  Q2:     ['Q2'],
  Q3:     ['Q3'],
  Q4:     ['Q4'],
  annual: ['Q1', 'Q2', 'Q3', 'Q4'],
};

export function quartersForPeriod(period: EprPeriod): Array<'Q1' | 'Q2' | 'Q3' | 'Q4'> {
  return PERIOD_TO_QUARTERS[period];
}

/**
 * Derive the EPR period from the review-period ISO dates stamped on the
 * submission header. Used by the magic-link form, which only has the
 * header snapshot (not the cycle doc). Falls back to 'annual' (the
 * widest filter — never hides a KR by guessing too narrow).
 */
export function derivePeriodFromDates(fromIso?: string, toIso?: string): EprPeriod {
  if (!fromIso || !toIso) return 'annual';
  const from = new Date(fromIso);
  const to   = new Date(toIso);
  if (isNaN(from.getTime()) || isNaN(to.getTime())) return 'annual';
  const fromQ = Math.floor(from.getMonth() / 3) + 1;  // 1..4
  const toQ   = Math.floor(to.getMonth() / 3) + 1;
  if (fromQ === 1 && toQ >= 4) return 'annual';
  if (fromQ === 1 && toQ === 2) return 'H1';
  if (fromQ === 3 && toQ === 4) return 'H2';
  if (fromQ === toQ) return `Q${fromQ}` as EprPeriod;
  return 'annual';
}

// ─── User lookup ────────────────────────────────────────────────────

/**
 * Resolve an OKR Tracker user UID by email. Returns null if the user
 * doesn't exist in the OKR Tracker yet — the caller should treat that
 * as "no OKRs to sync" rather than an error.
 *
 * Email is the cross-app join key per the cascade plan; MS Graph
 * employeeId may differ from Firebase auth UID, but email is stable.
 *
 * Thin wrapper around `resolveUserByEmail` for callers that only need
 * the UID.
 */
export async function resolveOwnerIdByEmail(email: string): Promise<string | null> {
  const user = await resolveUserByEmail(email);
  return user?.uid ?? null;
}

/** Resolved OKR Tracker user — UID plus home-unit context for scoping. */
export interface ResolvedOkrTrackerUser {
  uid: string;
  /**
   * All unit ids associated with this user, in priority order:
   *   1. `users.{uid}.unitId` (single field if present)
   *   2. `users.{uid}.unitIds[]` (array field if present)
   *   3. Reverse lookup: `units WHERE leadUserId == uid` — units they
   *      LEAD (used by dept heads / superadmins who don't have an
   *      explicit unitId set on their user doc)
   * Empty array = user has no resolvable home unit; the picker should
   * offer a manual unit-filter fallback.
   */
  unitIds: string[];
  /** Display name(s) of the resolved units, in the same order as unitIds. */
  unitNames: string[];
  displayName?: string;
  /** Path the unit ids came from — surfaced in the UI so HR sees why. */
  unitSource: 'user_doc_unitId' | 'user_doc_unitIds' | 'leads_units' | 'none';
}

/**
 * Full user resolution by email. Tries three paths to figure out which
 * unit(s) the user belongs to, since OKR Tracker doesn't enforce
 * `users.unitId` — admins and pre-import users often have it missing.
 *
 * Returns null only if the email is not in /users at all. When found
 * but no unit is resolvable, returns the user with empty unitIds[]
 * and unitSource='none' so the UI can offer a manual filter.
 */
export async function resolveUserByEmail(email: string): Promise<ResolvedOkrTrackerUser | null> {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return null;

  // OKR Tracker users are stored with the literal email casing the
  // SSO provider returned; try lowercase first, then original.
  const candidates = [trimmed];
  if (email !== trimmed) candidates.push(email);

  let foundDoc: { id: string; data: { unitId?: string; unitIds?: string[]; displayName?: string } } | null = null;
  for (const candidate of candidates) {
    const q = query(
      collection(db, 'users'),
      where('email', '==', candidate),
      limit(1),
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const d = snap.docs[0];
      foundDoc = { id: d.id, data: d.data() as { unitId?: string; unitIds?: string[]; displayName?: string } };
      break;
    }
  }
  if (!foundDoc) return null;

  let unitIds: string[] = [];
  let unitSource: ResolvedOkrTrackerUser['unitSource'] = 'none';

  // Path 1 — single unitId on user doc
  if (typeof foundDoc.data.unitId === 'string' && foundDoc.data.unitId) {
    unitIds = [foundDoc.data.unitId];
    unitSource = 'user_doc_unitId';
  }
  // Path 2 — unitIds[] array on user doc
  else if (Array.isArray(foundDoc.data.unitIds) && foundDoc.data.unitIds.length > 0) {
    unitIds = foundDoc.data.unitIds.filter((u): u is string => typeof u === 'string' && u.length > 0);
    unitSource = 'user_doc_unitIds';
  }
  // Path 3 — reverse lookup: units this person leads
  else {
    try {
      const leadsSnap = await getDocs(query(
        collection(db, 'units'),
        where('leadUserId', '==', foundDoc.id),
      ));
      if (!leadsSnap.empty) {
        unitIds = leadsSnap.docs.map(d => d.id);
        unitSource = 'leads_units';
      }
    } catch { /* ignore — fall through to empty */ }
  }

  // Resolve unit names for display. Cheap (small N); silent on failure.
  const unitNames: string[] = [];
  for (const uid of unitIds) {
    try {
      const snap = await getDoc(doc(db, 'units', uid));
      if (snap.exists()) {
        const u = snap.data() as { name?: string };
        unitNames.push(u.name ?? uid);
      } else {
        unitNames.push(uid);
      }
    } catch {
      unitNames.push(uid);
    }
  }

  return {
    uid:         foundDoc.id,
    unitIds,
    unitNames,
    displayName: foundDoc.data.displayName,
    unitSource,
  };
}

// ─── Main fetch ─────────────────────────────────────────────────────

export type EmployeeLevelForSync = 'general' | 'supervisor' | 'dept_head';

export interface FetchKrsForEmployeeInput {
  /** Employee's email — the cross-app join key. */
  email: string;
  /** Cycle year — only KRs in matching objective.cycle = String(year) are returned. */
  year: number;
  /** EPR period determines the quarter overlap filter. */
  period: EprPeriod;
  /**
   * Employee level — drives which buckets are pulled:
   *   - 'dept_head' / 'supervisor': owner + cascaded, scoped to home unit
   *   - 'general': cascaded ONLY (no owner bucket — regular employees
   *     don't "own" KRs by virtue of department membership; they only
   *     work on what's explicitly cascaded to them)
   * Defaults to 'dept_head' for backwards compat when level isn't passed.
   */
  level?: EmployeeLevelForSync;
  /**
   * When true (default for non-general levels), filter the owner bucket
   * to KRs aligned with the employee's home unit. Admins / system users
   * often have ownerId on cross-department KRs that aren't relevant.
   * Set to false to bypass (HR override). Ignored for general level
   * since the owner bucket is skipped entirely.
   */
  scopeByUnit?: boolean;
  /**
   * Optional manual unit-id allowlist. When provided, the filter uses
   * THIS set instead of the auto-resolved units from the user doc.
   * Used when the user has no resolvable home unit and HR picks
   * manually in the picker UI.
   */
  manualUnitIds?: string[];
}

export interface FetchKrsForEmployeeResult {
  /** Resolved OKR Tracker UID, or null when the user isn't in /users. */
  ownerId: string | null;
  /** KRs ready to be rendered in the picker. */
  krs: SyncedKr[];
  /**
   * Diagnostic: number of KRs found before filtering by year + quarter.
   * Helps HR understand "I have 8 KRs in OKR Tracker but only 3 show up here".
   */
  rawCount: number;
  /** Resolved home-unit context, if the user is in OKR Tracker. */
  user?: {
    uid: string;
    unitIds: string[];
    unitNames: string[];
    unitSource: 'user_doc_unitId' | 'user_doc_unitIds' | 'leads_units' | 'none';
  };
  /**
   * Distinct unit ids observed across the OWNED KRs (pre-filter). Lets
   * the picker UI offer HR a manual unit filter when auto-resolution
   * fails. Comes back even when scopeByUnit is true so HR can switch.
   */
  observedUnits: Array<{ unitId: string; count: number }>;
  /** Diagnostic counts per bucket — surfaced in the picker for HR audit. */
  diagnostics: {
    ownedKrCount: number;        // KRs in keyResults with ownerId match
    cascadeMatchCount: number;   // cascadedTasks rows matching this employee
    cascadedKrCount: number;     // distinct parent KRs from those cascades
    droppedWrongYear: number;    // KRs dropped by cycle.year mismatch
    droppedWrongPeriod: number;  // KRs dropped by quarter overlap
    droppedOutOfUnit: number;    // KRs dropped because the employee's
                                 // home unit doesn't match any of the KR's
                                 // unit/unitIds/leadDivisionId fields
    droppedArchived: number;     // parent objective archived or missing
  };
}

/**
 * Fetch KRs that belong to the employee — both OWNED KRs and KRs that
 * have been CASCADED to them via okr.rascalrepublic.com. Scoped to the
 * EPR cycle's year and period's quarter overlap.
 *
 * Two bucket sources, merged + de-duped by KR id (priority: owner wins
 * over assigned, since owning is "stronger" than being a cascade
 * recipient):
 *
 *   Bucket 'owner'    — keyResults WHERE ownerId == resolved UID
 *   Bucket 'assigned' — cascadedTasks WHERE assigneeUid == UID OR
 *                       assigneeEmail == email — then JOIN to keyResults
 *                       by parentKrId
 *
 * Returns diagnostic counts so the picker can show "X owned + Y assigned"
 * and HR can audit why a particular KR isn't appearing (year mismatch,
 * outside period, etc.).
 */
export async function fetchKrsForEmployee(
  input: FetchKrsForEmployeeInput,
): Promise<FetchKrsForEmployeeResult> {
  const emailNorm = input.email.trim().toLowerCase();
  const user = await resolveUserByEmail(input.email);
  const ownerId = user?.uid ?? null;

  const level: EmployeeLevelForSync = input.level ?? 'dept_head';

  // General employees don't get the owner bucket — they only own KRs
  // that were explicitly cascaded to them. Skipping the owner query
  // avoids surfacing department-wide KRs they don't personally work on.
  const includeOwnerBucket = level === 'dept_head' || level === 'supervisor';

  // Default ON — admins / shared system users frequently have ownerId on
  // dozens of cross-department KRs that aren't relevant to their EPR.
  const scopeByUnit = input.scopeByUnit !== false;

  const yearStr = String(input.year);
  const allowedQuarters = new Set(quartersForPeriod(input.period));

  const diagnostics = {
    ownedKrCount:       0,
    cascadeMatchCount:  0,
    cascadedKrCount:    0,
    droppedWrongYear:   0,
    droppedWrongPeriod: 0,
    droppedOutOfUnit:   0,
    droppedArchived:    0,
  };

  // Cache parent objective per call so each /objectives doc is read once.
  const objCache = new Map<string, OkrTrackerObjective | null>();
  const getObjective = async (id: string): Promise<OkrTrackerObjective | null> => {
    if (objCache.has(id)) return objCache.get(id) ?? null;
    const snap = await getDoc(doc(db, 'objectives', id));
    const v = snap.exists() ? (snap.data() as OkrTrackerObjective) : null;
    objCache.set(id, v);
    return v;
  };

  // De-dup keyed by KR id — owner bucket wins over assigned if both match.
  const seen = new Map<string, SyncedKr>();

  // Build the set of unit ids that act as the scope filter:
  //   - manualUnitIds wins if HR passed an explicit override
  //   - otherwise, the auto-resolved unitIds from the user doc
  // Empty set = filter is disabled (we let everything through).
  const scopeUnitSet: Set<string> | undefined =
    !scopeByUnit
      ? undefined
      : input.manualUnitIds && input.manualUnitIds.length > 0
        ? new Set(input.manualUnitIds)
        : user && user.unitIds.length > 0
          ? new Set(user.unitIds)
          : undefined;

  // Track which units appeared on owned KRs so the UI can offer manual
  // selection when auto-scope fails.
  const observedUnitCounts = new Map<string, number>();

  // ── Bucket 'owner' — keyResults.ownerId == UID ────────────────────
  // Only pulled for dept_head / supervisor levels. General employees
  // skip this bucket entirely — see EmployeeLevelForSync doc above.
  if (ownerId && includeOwnerBucket) {
    const ownerSnap = await getDocs(query(
      collection(db, 'keyResults'),
      where('ownerId', '==', ownerId),
    ));
    diagnostics.ownedKrCount = ownerSnap.size;
    for (const d of ownerSnap.docs) {
      const kr = d.data() as OkrTrackerKeyResult;
      // Survey observed units (every owned KR contributes, regardless
      // of whether it ends up filtered out).
      const krUnits = [kr.unitId, kr.leadDivisionId, ...(kr.unitIds ?? [])]
        .filter((u): u is string => typeof u === 'string' && u.length > 0);
      for (const u of krUnits) {
        observedUnitCounts.set(u, (observedUnitCounts.get(u) ?? 0) + 1);
      }

      const synced = await tryBuildSynced(d.id, kr, 'owner',
        yearStr, allowedQuarters, scopeUnitSet,
        getObjective, diagnostics);
      if (synced) seen.set(d.id, synced);
    }
  }

  // ── Bucket 'assigned' — cascadedTasks → parentKrId ────────────────
  // Query by both assigneeUid (when resolvable) AND assigneeEmail to
  // cover cascades issued before the recipient signed in.
  const cascadeQueries: Promise<Array<{ parentKrId: string }>>[] = [
    getDocs(query(
      collection(db, 'cascadedTasks'),
      where('assigneeEmail', '==', emailNorm),
    )).then(s => s.docs.map(d => ({ parentKrId: (d.data() as { parentKrId: string }).parentKrId }))),
  ];
  if (ownerId) {
    cascadeQueries.push(
      getDocs(query(
        collection(db, 'cascadedTasks'),
        where('assigneeUid', '==', ownerId),
      )).then(s => s.docs.map(d => ({ parentKrId: (d.data() as { parentKrId: string }).parentKrId }))),
    );
  }
  const cascadeResults = (await Promise.all(cascadeQueries)).flat();
  diagnostics.cascadeMatchCount = cascadeResults.length;

  // Unique parent KR ids
  const cascadedKrIds = Array.from(new Set(cascadeResults.map(c => c.parentKrId).filter(Boolean)));
  diagnostics.cascadedKrCount = cascadedKrIds.length;

  for (const krId of cascadedKrIds) {
    if (seen.has(krId)) continue; // owner bucket wins
    const krSnap = await getDoc(doc(db, 'keyResults', krId));
    if (!krSnap.exists()) continue;
    const kr = krSnap.data() as OkrTrackerKeyResult;
    // Cascaded KRs are EXPLICITLY assigned to the employee, so we trust
    // the cascade and skip the unit scope filter (pass undefined).
    const synced = await tryBuildSynced(krId, kr, 'assigned',
      yearStr, allowedQuarters, undefined,
      getObjective, diagnostics);
    if (synced) seen.set(krId, synced);
  }

  // Resolve names for observed units so the UI can render the manual
  // picker meaningfully.
  const observedUnitsArr = Array.from(observedUnitCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([unitId, count]) => ({ unitId, count }));

  const out = Array.from(seen.values());

  // Sort: owners first, then assigned; within each bucket, by KR number.
  out.sort((a, b) => {
    if (a.bucket !== b.bucket) return a.bucket === 'owner' ? -1 : 1;
    return a.krNumber.localeCompare(b.krNumber);
  });

  const rawCount = diagnostics.ownedKrCount + diagnostics.cascadedKrCount;
  return {
    ownerId,
    krs:           out,
    rawCount,
    diagnostics,
    user:          user
      ? {
          uid:        user.uid,
          unitIds:    user.unitIds,
          unitNames:  user.unitNames,
          unitSource: user.unitSource,
        }
      : undefined,
    observedUnits: observedUnitsArr,
  };
}

/**
 * Internal helper — apply year, quarter, and (optionally) unit-scope
 * filters, then convert one raw KR doc into a SyncedKr. Returns null
 * when filtered out and updates the diagnostic counters in-place so
 * the picker can explain to HR exactly why a KR was excluded.
 *
 * `scopeUnitId` — when truthy, the KR must match the user's home unit
 * via `kr.unitId`, `kr.unitIds[]`, OR `kr.leadDivisionId`. Pass
 * undefined to skip the filter (used for cascaded KRs which are
 * explicitly assigned regardless of unit).
 */
async function tryBuildSynced(
  krId: string,
  kr: OkrTrackerKeyResult,
  bucket: SyncedKrBucket,
  yearStr: string,
  allowedQuarters: Set<'Q1' | 'Q2' | 'Q3' | 'Q4'>,
  scopeUnitSet: Set<string> | undefined,
  getObjective: (id: string) => Promise<OkrTrackerObjective | null>,
  diagnostics: {
    ownedKrCount: number;
    cascadeMatchCount: number;
    cascadedKrCount: number;
    droppedWrongYear: number;
    droppedWrongPeriod: number;
    droppedOutOfUnit: number;
    droppedArchived: number;
  },
): Promise<SyncedKr | null> {
  const obj = await getObjective(kr.objectiveId);
  if (!obj || obj.archived) {
    diagnostics.droppedArchived += 1;
    return null;
  }
  if (obj.cycle !== yearStr) {
    diagnostics.droppedWrongYear += 1;
    return null;
  }

  const quarters = (kr.quarters ?? []).filter(
    (q): q is 'Q1' | 'Q2' | 'Q3' | 'Q4' =>
      q === 'Q1' || q === 'Q2' || q === 'Q3' || q === 'Q4',
  );
  // If KR has no quarters set, err on side of inclusion (annual EPR
  // pulls everything; quarter-bound EPR pulls KRs whose quarters
  // intersect the period).
  const matchesPeriod =
    quarters.length === 0
      || quarters.some(q => allowedQuarters.has(q));
  if (!matchesPeriod) {
    diagnostics.droppedWrongPeriod += 1;
    return null;
  }

  // Unit scope filter — main noise reducer for admin/system users.
  // KR is in-scope if ANY of its unit fields matches ANY id in the set.
  if (scopeUnitSet && scopeUnitSet.size > 0) {
    const krUnits: string[] = [];
    if (kr.unitId) krUnits.push(kr.unitId);
    if (kr.leadDivisionId) krUnits.push(kr.leadDivisionId);
    if (Array.isArray(kr.unitIds)) krUnits.push(...kr.unitIds);
    const inScope = krUnits.some(u => scopeUnitSet.has(u));
    if (!inScope) {
      diagnostics.droppedOutOfUnit += 1;
      return null;
    }
  }

  return {
    krId,
    objectiveId:    kr.objectiveId,
    objectiveTitle: obj.title ?? '(untitled objective)',
    krNumber:       kr.krNumber ?? '',
    title:          kr.title,
    target:         kr.sourceOfVerification,
    quarters,
    bucket,
  };
}

// ─── Convert to OkrSeed ─────────────────────────────────────────────

import type { OkrSeed } from './hrCycleLauncher';

/**
 * Convert a SyncedKr into the OkrSeed shape that the cycle launcher
 * expects. Preserves the OKR Tracker KR id as the seed id so the EPR
 * item retains its origin reference.
 */
export function toOkrSeed(kr: SyncedKr): OkrSeed {
  const name = kr.krNumber
    ? `${kr.krNumber} — ${kr.title}`
    : kr.title;
  return {
    id:     kr.krId,
    name,
    target: kr.target,
  };
}

// Re-export for consumers
export type { OkrTrackerUser, OkrTrackerKeyResult };
